<template>
  <div class="g2048">
    <!-- 头部：标题 / 得分 / 最佳 / 重开 -->
    <div class="g2048-head">
      <div class="g2048-title">🍬 糖果 2048</div>
      <div class="g2048-stats">
        <div class="g2048-stat">
          <span class="g2048-stat-label">得分</span>
          <b class="g2048-stat-num">{{ score }}</b>
        </div>
        <div class="g2048-stat">
          <span class="g2048-stat-label">最佳</span>
          <b class="g2048-stat-num">{{ best }}</b>
        </div>
      </div>
      <button type="button" class="g2048-restart" @click="restart">🔄 重新开始</button>
    </div>

    <!-- 棋盘 -->
    <div class="g2048-board">
      <div v-for="(row, r) in board" :key="r" class="g2048-row">
        <div
            v-for="(cell, c) in row"
            :key="c"
            class="g2048-cell"
            :class="'t' + cell">
          <span v-if="cell" class="g2048-num">{{ cell }}</span>
        </div>
      </div>

      <!-- 结束 / 达成遮罩 -->
      <div v-if="over" class="g2048-mask">
        <div class="g2048-mask-emoji">😢</div>
        <div class="g2048-mask-text">没有可以合并的糖果啦</div>
        <button type="button" class="g2048-mask-btn" @click="restart">再来一局</button>
      </div>
      <div v-else-if="won" class="g2048-mask">
        <div class="g2048-mask-emoji">🎉</div>
        <div class="g2048-mask-text">达成 2048 ！</div>
        <button type="button" class="g2048-mask-btn" @click="continuePlay">继续挑战</button>
      </div>
    </div>

    <!-- 方向按钮 -->
    <div class="g2048-keys">
      <button type="button" class="g2048-key g2048-key-up" @click="move('up')">↑</button>
      <button type="button" class="g2048-key g2048-key-left" @click="move('left')">←</button>
      <button type="button" class="g2048-key g2048-key-down" @click="move('down')">↓</button>
      <button type="button" class="g2048-key g2048-key-right" @click="move('right')">→</button>
    </div>
    <p class="g2048-tip">键盘方向键 / WASD 控制滑动</p>
  </div>
</template>

<script>
/* =====================================================
 * 糖果 2048（粉甜主题）
 * 规则：方向键滑动，相同数字合并翻倍，达成 2048 胜利
 * ===================================================== */
const SIZE = 4

export default {
  name: 'Game2048',
  data () {
    return {
      board: [],
      score: 0,
      best: 0,
      over: false,
      won: false
    }
  },
  created () {
    this.best = parseInt(localStorage.getItem('g2048_best') || '0', 10)
    this.initBoard()
  },
  mounted () {
    window.addEventListener('keydown', this.onKey)
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.onKey)
  },
  methods: {
    /* ---------- 初始化 ---------- */
    initBoard () {
      this.board = Array.from({ length: SIZE }, () => Array(SIZE).fill(0))
      this.score = 0
      this.over = false
      this.won = false
      this.addTile()
      this.addTile()
    },
    restart () {
      this.initBoard()
    },
    continuePlay () {
      this.won = false
    },

    /* ---------- 生成新糖果 ---------- */
    addTile () {
      const empty = []
      for (let r = 0; r < SIZE; r++) {
        for (let c = 0; c < SIZE; c++) {
          if (!this.board[r][c]) empty.push([r, c])
        }
      }
      if (!empty.length) return
      const idx = Math.floor(Math.random() * empty.length)
      const [r, c] = empty[idx]
      this.$set(this.board[r], c, Math.random() < 0.9 ? 2 : 4)
    },

    /* ---------- 键盘 ---------- */
    onKey (e) {
      const map = {
        ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right',
        w: 'up', s: 'down', a: 'left', d: 'right',
        W: 'up', S: 'down', A: 'left', D: 'right'
      }
      const dir = map[e.key]
      if (!dir) return
      e.preventDefault()
      this.move(dir)
    },

    /* ---------- 核心移动逻辑 ---------- */
    slide (row) {
      const arr = row.filter(v => v)
      const out = []
      let i = 0
      while (i < arr.length) {
        if (i + 1 < arr.length && arr[i] === arr[i + 1]) {
          out.push(arr[i] * 2)
          this.score += arr[i] * 2
          i += 2
        } else {
          out.push(arr[i])
          i++
        }
      }
      while (out.length < SIZE) out.push(0)
      return out
    },
    move (dir) {
      if (this.over) return
      const old = this.board.map(row => row.join(','))
      let newBoard
      if (dir === 'left') {
        newBoard = this.board.map(row => this.slide(row))
      } else if (dir === 'right') {
        newBoard = this.board.map(row => this.slide(row.slice().reverse()).reverse())
      } else {
        // 上 / 下：按列处理
        const cols = []
        for (let c = 0; c < SIZE; c++) {
          const col = this.board.map(row => row[c])
          cols.push(dir === 'up' ? this.slide(col) : this.slide(col.slice().reverse()).reverse())
        }
        newBoard = Array.from({ length: SIZE }, (_, r) => cols.map(col => col[r]))
      }
      const changed = newBoard.some((row, r) => row.join(',') !== old[r])
      if (!changed) return
      this.board = newBoard
      this.checkWin()
      this.addTile()
      this.checkOver()
      if (this.score > this.best) {
        this.best = this.score
        localStorage.setItem('g2048_best', String(this.best))
      }
    },

    /* ---------- 判断 ---------- */
    checkWin () {
      if (this.won) return
      for (let r = 0; r < SIZE; r++) {
        for (let c = 0; c < SIZE; c++) {
          if (this.board[r][c] === 2048) {
            this.won = true
            return
          }
        }
      }
    },
    checkOver () {
      for (let r = 0; r < SIZE; r++) {
        for (let c = 0; c < SIZE; c++) {
          if (!this.board[r][c]) return
          if (c + 1 < SIZE && this.board[r][c] === this.board[r][c + 1]) return
          if (r + 1 < SIZE && this.board[r][c] === this.board[r + 1][c]) return
        }
      }
      this.over = true
    }
  }
}
</script>

<style scoped>
.g2048 {
  font-family: "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
  color: #6b4a5a;
  max-width: 480px;
  margin: 0 auto;
}
.g2048-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
}
.g2048-title {
  font-size: 22px;
  font-weight: 800;
  color: #e25b96;
  letter-spacing: 1px;
}
.g2048-stats {
  display: flex;
  gap: 10px;
}
.g2048-stat {
  min-width: 76px;
  text-align: center;
  padding: 7px 12px;
  border-radius: 12px;
  background: #fff7fb;
  border: 1px solid #ffddea;
}
.g2048-stat-label {
  display: block;
  font-size: 11px;
  color: #c490a8;
}
.g2048-stat-num {
  font-size: 19px;
  color: #ff5fa0;
}
.g2048-restart {
  border: 2px solid #ffd3e4;
  border-radius: 999px;
  padding: 8px 18px;
  background: #fff;
  color: #b06a8a;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.25s ease;
}
.g2048-restart:hover {
  border-color: #ff6fa5;
  color: #e25b96;
  transform: translateY(-1px);
}

/* ---------- 棋盘 ---------- */
.g2048-board {
  position: relative;
  padding: 12px;
  border-radius: 20px;
  background: linear-gradient(160deg, #ffe3f0, #ffd3e6);
  border: 2px solid #ffc3da;
  box-shadow: 0 10px 30px rgba(255, 158, 198, 0.3);
}
.g2048-row {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}
.g2048-row:last-child {
  margin-bottom: 0;
}
.g2048-cell {
  flex: 1;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.5);
  box-shadow: inset 0 0 0 1px rgba(255, 195, 218, 0.6);
  transition: background 0.15s ease;
}
.g2048-num {
  font-size: clamp(16px, 4.2vw, 30px);
  font-weight: 800;
  animation: g2048-pop 0.18s ease;
}
@keyframes g2048-pop {
  0% { transform: scale(0.6); }
  60% { transform: scale(1.12); }
  100% { transform: scale(1); }
}

/* 糖果色阶 */
.g2048-cell.t2   { background: linear-gradient(135deg, #fff6fa, #ffeaf3); }
.g2048-cell.t2 .g2048-num { color: #d98aa6; }
.g2048-cell.t4   { background: linear-gradient(135deg, #ffe9f3, #ffd6e8); }
.g2048-cell.t4 .g2048-num { color: #d97ba0; }
.g2048-cell.t8   { background: linear-gradient(135deg, #ffd3e6, #ffc0d8); }
.g2048-cell.t8 .g2048-num { color: #d4658f; }
.g2048-cell.t16  { background: linear-gradient(135deg, #ffc0d8, #ffa9c9); }
.g2048-cell.t16 .g2048-num { color: #fff; text-shadow: 0 1px 4px rgba(200, 70, 120, 0.4); }
.g2048-cell.t32  { background: linear-gradient(135deg, #ffa9c9, #ff8fc7); }
.g2048-cell.t32 .g2048-num { color: #fff; text-shadow: 0 1px 4px rgba(200, 70, 120, 0.4); }
.g2048-cell.t64  { background: linear-gradient(135deg, #ff8fc7, #ff6fa5); }
.g2048-cell.t64 .g2048-num { color: #fff; text-shadow: 0 1px 4px rgba(200, 70, 120, 0.4); }
.g2048-cell.t128 { background: linear-gradient(135deg, #f26aa8, #e05597); }
.g2048-cell.t128 .g2048-num { color: #fff; text-shadow: 0 1px 4px rgba(160, 40, 90, 0.45); }
.g2048-cell.t256 { background: linear-gradient(135deg, #e05597, #cf4485); }
.g2048-cell.t256 .g2048-num { color: #fff; text-shadow: 0 1px 4px rgba(160, 40, 90, 0.45); }
.g2048-cell.t512 { background: linear-gradient(135deg, #cf4485, #bd3676); }
.g2048-cell.t512 .g2048-num { color: #fff; text-shadow: 0 1px 4px rgba(140, 30, 75, 0.5); }
.g2048-cell.t1024 { background: linear-gradient(135deg, #b72f6c, #a3265f); }
.g2048-cell.t1024 .g2048-num { color: #fff; text-shadow: 0 1px 4px rgba(120, 20, 60, 0.5); }
.g2048-cell.t2048 { background: linear-gradient(135deg, #ffd700, #ffab2e); box-shadow: 0 0 18px rgba(255, 200, 60, 0.55); }
.g2048-cell.t2048 .g2048-num { color: #a34b00; text-shadow: 0 1px 2px rgba(255, 255, 255, 0.5); }

/* ---------- 遮罩 ---------- */
.g2048-mask {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border-radius: 18px;
  background: rgba(255, 240, 247, 0.9);
  backdrop-filter: blur(2px);
  z-index: 5;
  animation: g2048-fade 0.3s ease;
}
@keyframes g2048-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}
.g2048-mask-emoji { font-size: 48px; animation: g2048-bounce 0.5s ease; }
@keyframes g2048-bounce {
  0% { transform: scale(0.4); }
  60% { transform: scale(1.15); }
  100% { transform: scale(1); }
}
.g2048-mask-text {
  font-size: 18px;
  font-weight: 800;
  color: #e25b96;
}
.g2048-mask-btn {
  border: none;
  border-radius: 999px;
  padding: 10px 26px;
  background: linear-gradient(135deg, #ff8fc7, #ff6fa5);
  color: #fff;
  font-size: 15px;
  cursor: pointer;
  box-shadow: 0 5px 14px rgba(255, 111, 165, 0.4);
  transition: transform 0.2s ease;
}
.g2048-mask-btn:hover { transform: translateY(-2px); }

/* ---------- 方向按钮 ---------- */
.g2048-keys {
  display: grid;
  grid-template-columns: repeat(3, 52px);
  grid-template-rows: repeat(2, 40px);
  gap: 8px;
  justify-content: center;
  margin-top: 16px;
}
.g2048-key {
  border: 2px solid #ffd3e4;
  border-radius: 12px;
  background: #fff;
  color: #e25b96;
  font-size: 18px;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.2s ease;
}
.g2048-key:hover {
  background: #ffeef6;
  border-color: #ff6fa5;
}
.g2048-key-up { grid-column: 2; }
.g2048-key-left { grid-row: 2; grid-column: 1; }
.g2048-key-down { grid-row: 2; grid-column: 2; }
.g2048-key-right { grid-row: 2; grid-column: 3; }
.g2048-tip {
  text-align: center;
  font-size: 12px;
  color: #c490a8;
  margin-top: 10px;
}

@media (max-width: 520px) {
  .g2048-num { font-size: 22px; }
  .g2048-title { font-size: 18px; }
}
</style>
